/**
 * Set status (DS v9, SettingsStatus-*): when a custom status clears itself and the suggestions.
 * Kept in step with MobileCh3 `src/screens/status/statusModel.ts`:
 * "Today" lasts until 9:00 tomorrow and "This week" until 9:00 next Monday.
 */
import moment from "moment-timezone";

export type ClearAfter = 'never' | '30m' | '1h' | '4h' | 'today' | 'week' | 'custom';

export const CLEAR_OPTIONS: { value: ClearAfter; label: string }[] = [
    {value: 'never', label: "Don't clear"},
    {value: '30m', label: '30 minutes'},
    {value: '1h', label: '1 hour'},
    {value: '4h', label: '4 hours'},
    {value: 'today', label: 'Today'},
    {value: 'week', label: 'This week'},
    {value: 'custom', label: 'Choose date and time'},
];

const nineAm = (m: moment.Moment) => m.hours(9).minutes(0).seconds(0).milliseconds(0);

/** When the status clears, or null for "Don't clear". */
export const statusDueDate = (key: ClearAfter, now: Date, custom?: Date): Date | null => {
    const m = moment(now);
    switch (key) {
        case 'never':
            return null;
        case '30m':
            return m.add(30, 'minutes').toDate();
        case '1h':
            return m.add(1, 'hour').toDate();
        case '4h':
            return m.add(4, 'hours').toDate();
        case 'today':
            return nineAm(m.add(1, 'day')).toDate();
        case 'week':
            return nineAm(m.add(1, 'week').startOf('isoWeek')).toDate();
        case 'custom':
            return custom ?? null;
    }
};

export interface StatusSuggestion {
    /** Hex code point(s), as the backend stores the icon. */
    icon: string;
    name: string;
    clearAfter: ClearAfter;
}

export const SUGGESTIONS: StatusSuggestion[] = [
    {icon: '1F4C5', name: 'In a meeting', clearAfter: '1h'},
    {icon: '1F68C', name: 'Commuting', clearAfter: '30m'},
    {icon: '1F957', name: 'Lunch time', clearAfter: '1h'},
    {icon: '1F912', name: 'Out sick', clearAfter: 'today'},
    {icon: '1F334', name: 'On vacation', clearAfter: 'never'},
];

export const clearLabel = (key: ClearAfter) => CLEAR_OPTIONS.find((o) => o.value === key)?.label ?? '';

export const NAME_MAX = 160;

export interface CustomStatus {
    name?: string | null;
    icon?: string | null;
    dueDate?: string | null;
}

/** A status counts while it has an icon and has not passed its end. */
export const isStatusActive = (status: CustomStatus | null | undefined, now = new Date()): boolean => {
    if (!status?.icon) return false;
    if (!status.dueDate) return true;
    return new Date(status.dueDate) > now;
};
